import { styled } from '@mui/material/styles';
import ReactDatePicker, { ReactDatePickerProps } from 'react-datepicker';

interface CalendarBaseProps<WithRange extends boolean | undefined = undefined>
	extends ReactDatePickerProps<never, WithRange> {
	advanced?: boolean;
}

export const CalendarBase = styled(
	<WithRange extends boolean | undefined = undefined>(props: CalendarBaseProps<WithRange>) => {
		const { advanced, ...rest } = props;

		return (
			<ReactDatePicker
				{...rest}
				selectsRange={props.selectsRange}
				calendarClassName={props.className}
				popperProps={{
					strategy: 'fixed',
					placement: 'bottom-start',
					modifiers: [
						{
							name: 'offset',
							options: {
								offset: advanced ? [-220, 0] : [-10, 0],
							},
						},
					],
				}}
			/>
		);
	}
)<{ advanced?: boolean }>(({ theme, advanced }) => ({
	'&.react-datepicker': {
		borderRadius: 12,
		backgroundColor: theme.colors.white,
		border: `1px solid ${theme.colors.blueTransparent}`,
		boxShadow: `0px 4px 4px rgba(68, 71, 77, 0.08)`,
		position: 'absolute',
		overflow: 'auto',
		fontSize: '12px',
	},
	'&.custom-calendar-with-menu': {
		display: 'flex',
		flexDirection: 'row',

		'.calendar-menu': {
			borderRight: `1px solid ${theme.colors.divider}`,
			padding: '24px 16px 24px 24px',
			minWidth: '130px',
			backgroundColor: theme.colors.white,
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			gap: 2,
		},

		'.calendar-content': {
			flex: 1,
		},
	},

	'& .react-datepicker__input-time-container': {
		margin: '-8px 0 0 0',
		padding: '0 24px 24px 24px',

		'.react-datepicker-time__input-container .react-datepicker-time__input': {
			input: {
				width: '100%',
			},
			margin: 0,
			alignItems: 'center',
			display: 'flex',
		},
	},

	'& .react-datepicker__header': {
		backgroundColor: theme.colors.white,
		margin: '18px 24px 0 24px',
		padding: 0,
		borderBottom: `1px solid ${theme.colors.divider}`,

		'.react-datepicker__day-names': {
			margin: 0,
			marginBottom: 4,

			'.react-datepicker__day-name': {
				fontFamily: '"Rubik", sans-serif',
				fontSize: '12px',
				fontWeight: 400,
				display: 'inline-flex',
				lineHeight: '120%',
				alignItems: 'center',
				justifyContent: 'center',
				textAlign: 'center',
				textTransform: 'capitalize',
				width: 32,
				height: 32,
				margin: 0,
				marginRight: 8,

				'&:last-child': {
					marginRight: 0,
				},
			},
		},
	},

	'& .react-datepicker__month': {
		margin: 0,
		padding: '16px 24px 24px 24px',

		'.react-datepicker__week': {
			marginBottom: 0,

			'&:last-child': {
				marginBottom: 0,
			},

			'.react-datepicker__day': {
				...theme.typography.body2,
				textAlign: 'center',
				margin: 0,
				marginLeft: 8,
				width: 32,
				height: 32,
				borderRadius: 12,
				display: 'inline-flex',
				justifyContent: 'center',
				alignItems: 'center',
				transition: theme.transitions.create(['background-color', 'color'], {
					duration: theme.transitions.duration.short,
				}),

				'&--keyboard-selected': {
					backgroundColor: theme.colors.blueTransparent,
				},

				'&--outside-month': {
					color: theme.colors.blueTransparent,
				},

				'&--selected, &--in-range, &--in-selecting-range': {
					color: theme.colors.black,
					backgroundColor: '#E2EEF5',
				},

				'&--range-start, &--range-end, &--selecting-range-start, &--selecting-range-end': {
					color: theme.colors.white,
					backgroundColor: theme.colors.lightBlue,
					borderRadius: 12,
				},

				'&--today': {
					fontWeight: 700,
				},

				'&--disabled': {
					backgroundColor: theme.colors.grayLight,
					color: theme.colors.grayLight,
				},

				'&:first-of-type': {
					marginLeft: 0,
				},
			},
		},
	},

	'& .react-datepicker__month-container': {
		margin: '0 0 0 0',
		padding: advanced ? '24px 16px' : '24px',

		'.react-datepicker__header': {
			margin: '0 0 16px 0',
		},

		'.react-datepicker__month': {
			display: 'flex',
			flexDirection: 'column',
			gap: '8px',
			padding: 0,

			'.react-datepicker__month-wrapper': {
				display: 'flex',
				gap: '8px',

				'.react-datepicker__month-text': {
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					minWidth: '62px',
					maxWidth: '62px',
					minHeight: '32px',
					borderRadius: '12px',
					borderWidth: '1px',
					borderStyle: 'solid',
					borderColor: 'white',
					margin: 0,

					'&.react-datepicker__month-text--in-selecting-range': {
						background: theme.colors.white,
						borderColor: theme.colors.lightBlue,
					},

					'&:hover': {
						background: 'rgba(36, 57, 106, 0.05)',
						borderColor: theme.colors.lightBlue,
					},

					'&.react-datepicker__month-text--in-range, &.react-datepicker__month-text--selected': {
						background: theme.colors.lightBlue,
						borderColor: theme.colors.lightBlue,

						'&:hover': {
							background: theme.colors.lightBlue,
							borderColor: theme.colors.lightBlue,
						},

						'& > span': {
							color: 'white',
						},
					},
				},
			},
		},
	},
}));
